import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { FitnessReservationsDataService, FitnessReservationsColumnsService } from '../../services';
import { DialogFitnessReservationFormComponent } from './fitness-reservation-form/fitness-reservation-form.component';
import { DialogFitnessReservationDetailsComponent } from './fitness-reservation-details/fitness-reservation-details.component';
import { FitnessReservation, FitnessReservationsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-fitness-reservations',
  templateUrl: './fitness-reservations.component.html',
  styleUrls: ['./fitness-reservations.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class FitnessReservationsComponent implements OnInit, OnDestroy {

  private fitnessReservationAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessReservationEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessReservationDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public fitnessReservationToDelete: FitnessReservation = {};
  public fitnessReservations: FitnessReservation[] = [];
  public fitnessReservation: FitnessReservation = {};
  public selectedFitnessReservations: FitnessReservation[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private fitnessReservationsDataService: FitnessReservationsDataService,
    private fitnessReservationsColumnsService: FitnessReservationsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('FitnessReservationsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Fitness Reservations - Management');
    this.primengConfig.ripple = true;
    this.getFitnessReservations();
  }

  ngOnDestroy(): void {
    console.log('FitnessReservationsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getFitnessReservations(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.fitnessReservationsDataService.getFitnessReservations(this.parameters).subscribe((response: FitnessReservationsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        response.results.map((fitnessReservation: any) => {
          fitnessReservation.fitnessSession = this.fitnessReservationsColumnsService.fitnessSessions.find((item: any) => {
            if (typeof fitnessReservation.fitnessSession !== 'undefined') {
              return item.id === fitnessReservation.fitnessSession.id;
            }
            return false;
          });
        });
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.fitnessReservations = response.results;
        } else {
          this.fitnessReservations = this.fitnessReservations.concat(response.results);
        }
        if (response.inlineCount > this.fitnessReservations.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getFitnessReservations();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSRESERVATIONSNOTLOADED')});
    });
  }

  importFitnessReservations(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.fitnessReservationsDataService.addFitnessReservations(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSRESERVATIONSCREATED')});
                if (proceed === j) {
                  this.getFitnessReservations();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSRESERVATIONSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSRESERVATIONSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.fitnessReservationsDataService.updateFitnessReservations(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSRESERVATIONSUPDATED')});
                if (proceed === j) {
                  this.getFitnessReservations();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSRESERVATIONSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSRESERVATIONSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.fitnessReservationsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('fitnessReservations.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedFitnessReservations() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Fitness Reservations?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.fitnessReservationsDataService.deleteFitnessReservations(this.selectedFitnessReservations).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Fitness Reservations Deleted', life: 3000});
            this.getFitnessReservations();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Reservations not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Reservations not deleted'});
        });
        this.selectedFitnessReservations = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.fitnessReservations, this.columns, 'FitnessReservations');
  }

  exportExcel() {
    ToolsService.exportExcel(this.fitnessReservations, this.columns, 'FitnessReservations');
  }

  exportCsv() {
    ToolsService.exportCsv(this.fitnessReservations, this.columns, 'FitnessReservations');
  }

  refresh() {
    this.fitnessReservationsDataService.clearCache();
    this.getFitnessReservations();
  }

  fitnessReservationAdd(): void {
    if (!this.navigation.getParamValue('fitnessReservations.addInDialog', true)) {
      this.router.navigate(['/fitness-reservations/add']);
      return;
    }
    this.fitnessReservationAddDialog = this.dialogService.open(DialogFitnessReservationFormComponent, {
      header: '', // this.translate.instant('NEWFITNESSRESERVATION'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        fitnessReservation: {id: null},
        fitnessSessions: this.fitnessReservationsColumnsService.fitnessSessions,
        members: this.fitnessReservationsColumnsService.members,
        users: this.fitnessReservationsColumnsService.users,
      },
    });

    this.fitnessReservationAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'FitnessReservation added'});
        this.getFitnessReservations();
      }
    });
  }

  fitnessReservationEdit(fitnessReservation: FitnessReservation): void {
    if (!this.navigation.getParamValue('fitnessReservations.editInDialog', true)) {
      this.router.navigate(['/fitness-reservations/edit/' + fitnessReservation.id]);
      return;
    }
    this.fitnessReservationEditDialog = this.dialogService.open(DialogFitnessReservationFormComponent, {
      header: '', // this.translate.instant('EDITFITNESSRESERVATION') + ' ' + fitnessReservation.id,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        fitnessReservation: fitnessReservation,
        fitnessSessions: this.fitnessReservationsColumnsService.fitnessSessions,
        members: this.fitnessReservationsColumnsService.members,
        users: this.fitnessReservationsColumnsService.users,
      },
    });

    this.fitnessReservationEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'FitnessReservation updated'});
        this.getFitnessReservations();
      }
    });
  }

  fitnessReservationDetails(fitnessReservation: FitnessReservation): void {
    if (!this.navigation.getParamValue('fitnessReservations.detailsInDialog', true)) {
      this.router.navigate(['/fitness-reservations/details/' + fitnessReservation.id]);
      return;
    }
    this.fitnessReservationDetailsDialog = this.dialogService.open(DialogFitnessReservationDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        fitnessReservation: fitnessReservation,
      }
    });
  }

  fitnessReservationDelete(fitnessReservation: FitnessReservation): void {
    this.fitnessReservationToDelete = fitnessReservation;
    this.messageService.clear();
    this.messageService.add({
      key: 'fitnessReservationDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + fitnessReservation.id + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.fitnessReservationsDataService.deleteFitnessReservation(this.fitnessReservationToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Fitness Reservation deleted'});
      this.getFitnessReservations();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Reservation not deleted'});
    });
  }

  onDeleteReject(): void {
    this.fitnessReservationToDelete = {};
    this.messageService.clear('fitnessReservationDelete');
  }

}
